import { useCallback, useDebugValue } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AsyncEntity } from 'src/global';
import { RootState } from 'src/store';
import { AppDispatch } from 'src/index';
import { useUploadFileToBucket } from 'src/hooks/useUploadFileToBucket';
import { CompanyVideo } from 'src/features/companyVideos/companyVideos.types';
import { companyVideosSelectors, updateCompanyVideo } from 'src/features/companyVideos/companyVideos.state';
import { uploadVideoImage } from 'src/features/companyVideos/companyVideos.api';

export const useCompanyVideosState = (): RootState['companyVideos'] => {
  const state = useSelector<RootState, RootState['companyVideos']>(state => state.companyVideos);

  useDebugValue(state);

  return state;
};

export const useCompanyVideos = (): CompanyVideo[] => {
  const banners = useSelector<RootState, RootState['companyVideos']>(state => state.companyVideos);

  const result = companyVideosSelectors.selectAll(banners);

  useDebugValue(result);

  return result;
};

export const useCompanyVideoById = (id: string): AsyncEntity<CompanyVideo> | null => {
  const banners = useSelector<RootState, RootState['companyVideos']>(state => state.companyVideos);

  const result = companyVideosSelectors.selectById(banners, id) || null;

  useDebugValue(result);

  return result;
};

export type UseVideoImage = (params: { videoId: string; companyId: string }) => {
  progress: number;
  uploadVideoImage: (file: File) => void;
};

export const useVideoImage: UseVideoImage = ({ videoId, companyId }) => {
  const dispatch: AppDispatch = useDispatch();
  const video = useCompanyVideoById(videoId) as CompanyVideo;
  const [progress, uploadFile] = useUploadFileToBucket();

  const updatePackageImage = useCallback(
    async (file: File) => {
      const { name: filename } = file;
      const images = video.image || [];

      const credentials = await uploadVideoImage({
        videoId,
        companyId,
        data: { filename },
      });

      const { url, fields } = credentials.data;
      const { key } = fields;

      if (credentials.status < 400) {
        const response = await uploadFile({ ...credentials.data, file });

        if (response) {
          console.log([...images, `${url}/${key}`]);
          dispatch(
            updateCompanyVideo({
              companyId,
              videoId,
              data: { image: `${url}/${key}` },
            })
          );
        }
      }
    },
    [companyId, dispatch, videoId, uploadFile, video]
  );

  return { progress, uploadVideoImage: updatePackageImage };
};
